import React, { useState } from 'react';
import { BrowserProvider, Contract, parseUnits } from 'ethers';
import '../components/Gallery.css';

// Smart Contract Details
const CONTRACT_ADDRESS = '0xAFf29DA9C78AEAA00a3D9d39A313666313876F80';
const CONTRACT_ABI = [
  {
    inputs: [
      { internalType: 'address', name: 'recipient', type: 'address' },
      { internalType: 'string', name: 'tokenURI', type: 'string' },
      { internalType: 'uint256', name: 'price', type: 'uint256' },
      { internalType: 'uint256', name: 'supply', type: 'uint256' }
    ],
    name: 'mintNFT',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  }
];

const NFTUploader = () => {
  const [file, setFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [supply, setSupply] = useState('');
  const [status, setStatus] = useState('');
  const [contentType, setContentType] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);

  const handleFileUpload = (e) => setFile(e.target.files[0]);
  const handleAudioUpload = (e) => setAudioFile(e.target.files[0]);

  // Wallet connection handler
  const connectWallet = async () => {
    if (!window.ethereum) {
      setStatus('MetaMask is not installed. Please install it to connect your wallet.');
      return;
    }

    if (isConnecting) {
      setStatus('Connecting to wallet. Please wait...');
      return;
    }

    setIsConnecting(true);

    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      setWalletAddress(accounts[0]);
      setStatus('Wallet connected!');
    } catch (error) {
      if (error.code === -32002) {
        setStatus('Wallet connection request already in progress. Please check MetaMask.');
      } else {
        setStatus(`Error connecting wallet: ${error.message}`);
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const validateFields = () => {
    if (!walletAddress) return 'Please connect your wallet before minting.';
    if (contentType === 'pictureVideo') {
      if (!file || !price || !supply) return 'File upload, price, and supply are required.';
    } else if (contentType === 'audio') {
      if (!file || !audioFile || !title || !price || !supply) {
        return 'Audio file, visual file, title, price, and supply are required.';
      }
    } else if (contentType === 'writtenRecord') {
      if (!description || !price || !supply) return 'Description, price, and supply are required.';
    }
    return null;
  };

  const mintNFT = async () => {
    const validationError = validateFields();
    if (validationError) {
      setStatus(validationError);
      return;
    }
  
    try {
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const contract = new Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signer);
  
      let visualURI = '';
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        const uploadResponse = await fetch('https://api.pinata.cloud/pinning/pinFileToIPFS', {
          method: 'POST',
          headers: {
            'pinata_api_key': process.env.REACT_APP_PINATA_API_KEY,
            'pinata_secret_api_key': process.env.REACT_APP_PINATA_SECRET_API_KEY
          },
          body: formData,
        });
        const result = await uploadResponse.json();
        if (result.IpfsHash) visualURI = `https://gateway.pinata.cloud/ipfs/${result.IpfsHash}`;
      }
  
      let audioURI = '';
      if (audioFile) {
        const formData = new FormData();
        formData.append('file', audioFile);
        const uploadResponse = await fetch('https://api.pinata.cloud/pinning/pinFileToIPFS', {
          method: 'POST',
          headers: {
            'pinata_api_key': process.env.REACT_APP_PINATA_API_KEY,
            'pinata_secret_api_key': process.env.REACT_APP_PINATA_SECRET_API_KEY
          },
          body: formData,
        });
        const result = await uploadResponse.json();
        if (result.IpfsHash) audioURI = `https://gateway.pinata.cloud/ipfs/${result.IpfsHash}`;
      }
  
      const tokenURI = JSON.stringify({
        title,
        description,
        image: visualURI,
        audio: audioURI,
        type: contentType,
      });
  
      await contract.mintNFT(
        await signer.getAddress(),
        tokenURI,
        parseUnits(price, 'ether'),
        parseInt(supply)
      );
  
      const newNFT = {
        owner: walletAddress,
        title,
        description,
        image: visualURI,
        audio: audioURI,
        type: contentType,
        price,
        supply,
        timestamp: new Date().toISOString()
      };
  
      // Separate try block for backend POST
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_API_URL;
console.log("🌐 Backend URL:", backendUrl); // <-- ADD THIS LINE
if (!backendUrl) throw new Error("Backend URL is not defined.");
        await fetch(`${backendUrl}/api/nfts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newNFT),
        });
        setStatus('Minted and saved successfully!');
      } catch (err) {
        console.error("Backend save failed:", err);
        setStatus(`Minted, but failed to save: ${err.message}`);
      }
  
    } catch (err) {
      console.error("Minting failed:", err);
      setStatus(`Error during minting: ${err.message}`);
    }
  };
  
      

      

  return (
    <div className="uploader-container">
      <h2>Mint New NFT</h2>
      <button onClick={connectWallet} disabled={isConnecting}>
        {walletAddress
          ? `Connected: ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`
          : isConnecting
          ? 'Connecting...'
          : 'Connect Wallet'}
      </button>
      <select value={contentType} onChange={(e) => setContentType(e.target.value)}>
        <option value="">Select Content Type</option>
        <option value="pictureVideo">Picture/Video</option>
        <option value="writtenRecord">Written Record</option>
        <option value="audio">Audio</option>
      </select>

      {contentType === 'pictureVideo' && (
        <>
          <input type="file" accept="image/*,video/*" onChange={handleFileUpload} />
          <input type="text" placeholder="Title (Optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
          <textarea placeholder="Description (Optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
        </>
      )}

      {contentType === 'audio' && (
        <>
          <input type="file" accept="image/*,video/*" onChange={handleFileUpload} />
          <input type="file" accept="audio/*" onChange={handleAudioUpload} />
          <input type="text" placeholder="Title (Required)" value={title} onChange={(e) => setTitle(e.target.value)} />
          <textarea placeholder="Description (Optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
        </>
      )}

      {contentType === 'writtenRecord' && (
        <>
          <input type="text" placeholder="Title (Optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
          <textarea placeholder="Description (Required)" value={description} onChange={(e) => setDescription(e.target.value)} />
        </>
      )}

      <input type="number" placeholder="Price (ETH)" value={price} onChange={(e) => setPrice(e.target.value)} />
      <input type="number" placeholder="Supply" value={supply} onChange={(e) => setSupply(e.target.value)} />
      <button onClick={mintNFT}>Mint NFT</button>
      <p>{status}</p>
    </div>
  );
};

export default NFTUploader;
